import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileText,
  FileSignature,
  FileCheck,
  Briefcase,
  Receipt,
  Shield,
  Mail,
  Folder,
  FolderOpen,
  Search,
  Download,
  Eye,
  RefreshCw,
  Archive,
  ChevronRight,
  Lock,
  File
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  participation_agreements: FileText,
  cession_documents: FileSignature,
  terms_conditions: FileCheck,
  investor_mandate: Briefcase,
  tax_certificates: Receipt,
  fica_documents: Shield,
  correspondence: Mail,
  other: Folder
};

const CATEGORY_COLORS = {
  participation_agreements: 'bg-blue-100 text-blue-700',
  cession_documents: 'bg-purple-100 text-purple-700',
  terms_conditions: 'bg-green-100 text-green-700',
  investor_mandate: 'bg-amber-100 text-amber-700',
  tax_certificates: 'bg-red-100 text-red-700',
  fica_documents: 'bg-indigo-100 text-indigo-700',
  correspondence: 'bg-pink-100 text-pink-700',
  other: 'bg-gray-100 text-gray-700'
};

const DocumentVaultPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/categories`, { headers });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);

      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents?${params}`, { headers }),
        axios.get(`${API_URL}/api/documents/stats`, { headers })
      ]);

      setDocuments(docsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, token]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchDocuments();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleView = async (doc) => {
    try {
      await axios.post(`${API_URL}/api/documents/${doc.id}/view`, {}, { headers });
      setPreviewDoc(doc);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to log view:', error);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/${doc.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.document_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d.id));
    }
  };

  const getCategoryIcon = (categoryId) => {
    const Icon = CATEGORY_ICONS[categoryId] || Folder;
    return Icon;
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Unknown';
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredCategories = [
    { id: 'all', name: 'All Documents', icon: 'folder' },
    ...categories
  ];

  return (
    <div className="space-y-6" data-testid="document-vault-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Document Vault</h1>
          <p className="text-[#6C757D] mt-1">Access and manage all your investment documents</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedDocs.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-gray-200"
              data-testid="bulk-download-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Download Selected ({selectedDocs.length})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDocuments}
            className="border-gray-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1D2D2A]/10 flex items-center justify-center">
                <File className="h-5 w-5 text-[#1D2D2A]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]" data-testid="total-docs">
                  {stats?.total_documents || 0}
                </p>
                <p className="text-xs text-[#6C757D]">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {stats?.by_category?.participation_agreements || 0}
                </p>
                <p className="text-xs text-[#6C757D]">Agreements</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {stats?.by_category?.tax_certificates || 0}
                </p>
                <p className="text-xs text-[#6C757D]">Tax Certs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {stats?.by_category?.fica_documents || 0}
                </p>
                <p className="text-xs text-[#6C757D]">FICA Docs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Folder Tree */}
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Categories</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-4 pt-0">
                {filteredCategories.map((category) => {
                  const Icon = category.id === 'all' ? FolderOpen : getCategoryIcon(category.id);
                  const count = category.id === 'all' 
                    ? stats?.total_documents 
                    : stats?.by_category?.[category.id] || 0;
                  const isSelected = selectedCategory === category.id;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setSelectedDocs([]);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                        isSelected
                          ? 'bg-[#1D2D2A] text-white'
                          : 'text-[#212529] hover:bg-gray-100'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <Icon className={`h-5 w-5 ${isSelected ? '' : 'text-[#6C757D]'}`} />
                      <span className="flex-1 text-sm font-medium truncate">{category.name}</span>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${
                          isSelected 
                            ? 'bg-white/20 text-white' 
                            : 'bg-gray-100 text-[#6C757D]'
                        }`}
                      >
                        {count}
                      </Badge>
                      {isSelected && <ChevronRight className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Main Content - Document List */}
        <Card className="border-0 shadow-sm lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-lg font-semibold font-['Manrope']">
                {selectedCategory === 'all' ? 'All Documents' : getCategoryName(selectedCategory)}
              </CardTitle>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#ADB5BD]" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-gray-200"
                  data-testid="search-documents"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : documents.length === 0 ? (
              <div className="p-12 text-center">
                <Folder className="h-12 w-12 mx-auto text-[#ADB5BD] mb-4" />
                <p className="text-[#6C757D]">No documents found</p>
                {searchQuery && (
                  <Button 
                    variant="link" 
                    onClick={() => setSearchQuery('')} 
                    className="mt-2 text-[#18A37C]"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-10">
                        <Checkbox 
                          checked={selectedDocs.length === documents.length && documents.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Document Name</TableHead>
                      <TableHead>Related Loan</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => {
                      const CategoryIcon = getCategoryIcon(doc.category);
                      return (
                        <TableRow 
                          key={doc.id} 
                          className="hover:bg-gray-50"
                          data-testid={`doc-row-${doc.id}`}
                        >
                          <TableCell>
                            <Checkbox 
                              checked={selectedDocs.includes(doc.id)}
                              onCheckedChange={() => handleSelectDoc(doc.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[doc.category] || 'bg-gray-100'}`}>
                                <CategoryIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-[#212529]">{doc.document_name}</p>
                                  {doc.is_confidential && (
                                    <Lock className="h-3 w-3 text-[#6C757D]" />
                                  )}
                                </div>
                                {selectedCategory === 'all' && (
                                  <p className="text-xs text-[#6C757D]">{getCategoryName(doc.category)}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {doc.loan_reference ? (
                              <Badge variant="outline" className="text-xs border-gray-200">
                                {doc.loan_reference}
                              </Badge>
                            ) : (
                              <span className="text-[#ADB5BD] text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[#6C757D]">
                            {formatDate(doc.created_at)}
                          </TableCell>
                          <TableCell className="text-right text-[#6C757D]">
                            {formatFileSize(doc.file_size)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleView(doc)}
                                className="text-[#6C757D] hover:text-[#1D2D2A]"
                                data-testid={`view-btn-${doc.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                className="text-[#6C757D] hover:text-[#1D2D2A]"
                                data-testid={`download-btn-${doc.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              {previewDoc?.is_confidential && <Lock className="h-4 w-4 text-[#6C757D]" />}
              {previewDoc?.document_name}
            </DialogTitle>
            <DialogDescription>
              {getCategoryName(previewDoc?.category)} • {formatDate(previewDoc?.created_at)} • {formatFileSize(previewDoc?.file_size)}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-100 rounded-lg p-8 min-h-[500px] flex items-center justify-center">
            <div className="text-center">
              <FileText className="h-16 w-16 mx-auto text-[#6C757D] mb-4" />
              <p className="text-[#6C757D] mb-4">Document Preview</p>
              <p className="text-sm text-[#ADB5BD] mb-4">
                In production, the PDF would be rendered here using a PDF viewer component.
              </p>
              {previewDoc?.loan_reference && (
                <Badge variant="outline" className="mb-4">
                  Related to: {previewDoc.loan_reference}
                </Badge>
              )}
              <div className="pt-2">
                <Button
                  className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                  onClick={() => previewDoc && handleDownload(previewDoc)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentVaultPage;
